/**
 * `shell/navLinks.ts` — pure nav-strip + module-switcher data for `<AppShell/>`.
 *
 * Extracted from `AppShell.tsx` (behaviour-preserving). These are the
 * pure (testable) pieces of the shell chrome: the role-aware nav-strip
 * derivation, the module-switcher registry, and the edge-to-edge route
 * predicate. The rendered chrome stays in `AppShell.tsx`.
 *
 * ⚠ Task-mode seam: `deriveNavLinks` carries the `team_kind === "tasks"`
 * branch that reads `TEAM_APPS.tasks.navItems`. That branch is the shared
 * task-mode seam and is kept byte-identical to its original `AppShell.tsx`
 * form (#753 / B7.2). A `'scheduling'` team derives byte-identically to
 * before.
 */
import type { WorkspaceRole } from "../../lib/activeWorkspace";
import { TEAM_APPS, type TeamKind } from "../../lib/teamApps";

/**
 * IA-2.5 — role-aware right-side strip.
 *
 * Per `docs/design/ia-phase-2-implementation-plan.md` §3 IA-2.5 +
 * §7.2 brand-pillar check, the AppShell right-side strip diverges by
 * role on the active workspace:
 *
 *   - `member`   → [Timeline] [My schedule] [Team roster] (+ gated
 *                  [Swaps]) [Activity] [Statistics] — the canonical
 *                  list also carries a `Hours` entry that the desktop
 *                  strip folds away (#1016, declared `desktopFold` on
 *                  the entry) while the phone bottom-tab bar keeps it
 *   - `lead`     → [Home] [Roster] [People] [Setup] (#2048; it read
 *                  Team · Members · Manpower · Setup until then)
 *   - `co_lead`  → identical to lead (founder §4.1 default — co-leads
 *                  have lead-equivalent capabilities)
 *
 * (#1294 removed the account-scoped Billing entry from every strip —
 * the surface carried no value and its plan copy had drifted from the
 * per-component product shape.)
 *
 * Empty-memberships / no-active-workspace falls through to the empty
 * strip (only the workspace switcher's "+ Create new team" affordance
 * is reachable — the user has no teams to navigate within yet). This
 * is the trustable-pillar choice: a brand-new account on `/onboarding`
 * sees the chrome but no top-level surfaces that aren't yet meaningful
 * for them.
 *
 * `Cycles`/`Members`/`Setup` route under `/teams/<active>/...` so the
 * URL captures the workspace; `My schedule` stays at `/me` (worker-
 * side, workspace-agnostic — a worker in N teams sees their cross-
 * team schedule on a single page).
 *
 * Submit's top-level alias is removed per IA-redesign §3.1 — Submit
 * is now a cycle-internal tab and the email-invite link lands the
 * worker on the cycle's Submissions tab (`/cycles/:id/submit?...`).
 *
 * Focal-component discipline (per the engineering-lead saved memory
 * `feedback_ui_section_focal_component.md`):
 *
 *   - **Right-side strip — leads.** `Cycles` is the focal link (the
 *     daily-flow entry point — leads spend the bulk of their time on
 *     cycle dashboards). Members / Setup recede to supporting weight.
 *   - **Right-side strip — members.** `My schedule` is the focal link
 *     (the worker's primary destination — their roster). Cycles +
 *     the other supporting links recede.
 *
 * Visual prioritisation: the focal link in each role is rendered with
 * `font-medium`; supporting links are `font-normal`. Active-route
 * highlighting (`bg-bg-subtle`) still wins regardless of role-focal,
 * because the active route is the highest-attention signal of all.
 *
 * Identifier convention: `to` is the absolute path to navigate on
 * click; `label` is the rendered string (forbidden-word-scanned per
 * the brand-pillars memory); `focal` is the role-current most-
 * frequented entry per the focal-component memory; `end` controls
 * NavLink's `end` prop so deep sub-routes don't flag the parent link
 * as active.
 */
export interface NavLinkSpec {
  to: string;
  label: string;
  focal: boolean;
  end: boolean;
  /**
   * #1016 / #1645 — the entry is folded out of the DESKTOP right-side
   * strip (its capability is carried by another strip entry) while a
   * denser derivation keeps it. Declared as data ON the entry so one
   * canonical list serves every surface under a uniform rule, rather
   * than a function that returns different lists per caller (the
   * retired `desktopStrip` mode flag). No current entry sets it —
   * #1666 retired the member strip, which was its only user — but the
   * rule stays because it is how the lead strip will fold if it grows.
   */
  desktopFold?: boolean;
  /**
   * #2048 — other addresses that belong to this entry (the cycle pages
   * belong to Roster, every Setup page to Setup). See `isNavLinkActive`.
   */
  activePrefixes?: string[];
}

export function leadNavLinks(
  activeWorkspaceId: string | null,
  // #1282 — the per-team `manpower_planning` component. Since #2048 it no
  // longer adds a strip entry (Manpower is the People page's "Over time"
  // tab); the parameter stays so every caller keeps its shape.
  _manpowerPlanningEnabled = false,
): NavLinkSpec[] {
  if (!activeWorkspaceId) return [];
  const team = `/teams/${activeWorkspaceId}`;
  // #2048 — the founder (6 Oct 2026): one row of navigation. Members and
  // Manpower were the same people seen two ways; they are one People page
  // with two tabs now. #2903 — the founder (28 Oct 2026) overrode the
  // four-entry rule: Team (Stations, Access, Activity) is the fifth entry.
  // Home · Roster · People · Team · Setup.
  return [
    // The team's home page. `end` so it is not lit on every sub-page.
    { to: team, label: "Home", focal: true, end: true },
    // The date-window roster. Lit on the cycle pages too: they are the
    // roster for one cycle's dates.
    {
      to: `${team}/roster`,
      label: "Roster",
      focal: false,
      end: false,
      activePrefixes: ["/cycles/"],
    },
    // The People page (list + over time). `/manpower` redirects here.
    { to: `${team}/members`, label: "People", focal: false, end: false },
    // #2903 — who is on which station now, who can sign in, what changed.
    { to: `${team}/team`, label: "Team", focal: false, end: false },
    {
      to: `${team}/setup/sessions`,
      label: "Setup",
      focal: false,
      end: false,
      activePrefixes: [`${team}/setup`],
    },
  ];
}

/** #2048 — is `link` the current place? NavLink's own match, or any of
 *  the link's `activePrefixes` (pages that belong to it under another
 *  address). Pure, so the strip and the phone drawer agree. */
export function isNavLinkActive(link: NavLinkSpec, pathname: string, navLinkActive: boolean) {
  if (navLinkActive) return true;
  return (link.activePrefixes ?? []).some((prefix) => pathname.startsWith(prefix));
}

/** How specifically `link` claims `pathname`: the length of the longest
 *  matching address (its own `to`, exactly when `end`, else as a path
 *  prefix; or one of its `activePrefixes`), or -1 for no claim. */
function navLinkClaim(link: NavLinkSpec, pathname: string): number {
  const path = pathname.replace(/\/+$/, "") || "/";
  const own = (link.to.split("?")[0] ?? "").replace(/\/+$/, "") || "/";
  let best = -1;
  if (path === own || (!link.end && path.startsWith(`${own}/`))) best = own.length;
  for (const prefix of link.activePrefixes ?? []) {
    if (pathname.startsWith(prefix)) best = Math.max(best, prefix.length);
  }
  return best;
}

/**
 * #2172 — the ONE strip entry that is current for `pathname`: the most
 * specific claim wins (`navLinkClaim`), so two entries never light at once
 * (a tasks team's Tasks entry, a prefix of every Map page, no longer lights
 * beside Team on `/map/members`), and the gliding highlight always has
 * exactly one place to be. Returns the winner's `to`, or null.
 */
export function pickActiveNavLink(links: NavLinkSpec[], pathname: string): string | null {
  let best: NavLinkSpec | null = null;
  let bestClaim = -1;
  for (const link of links) {
    const claim = navLinkClaim(link, pathname);
    if (claim > bestClaim) {
      best = link;
      bestClaim = claim;
    }
  }
  return best ? best.to : null;
}

/** IA-2.5 — derive the strip from the active workspace's role. Pure
 *  function so tests can exercise the shape directly. Returns an empty
 *  array when there's no active workspace + no role (empty memberships
 *  / first paint before the resolver runs).
 *
 *  `swapRequestsEnabled` (#514 Layer 3) gates the member-side `Swaps`
 *  entry on whether the active team has the `swap_requests` component
 *  toggled on — defaults to `false` so the link stays hidden until the
 *  caller (AppShell) feeds a resolved feature-flag read in. Lead-side
 *  strip is unaffected; leads access swap oversight from the cycle
 *  workspace toolbar's Swaps button → `<SwapsModal/>` (re-homed
 *  `<SwapsTab/>`, #777), gated by the same `swap_requests` flag.
 *
 *  `teamKind` (#753) — when the active workspace is a `'tasks'` team,
 *  the strip reads from `TEAM_APPS['tasks'].navItems` (Map · Members ·
 *  Settings) regardless of role. A scheduling team is byte-identical to
 *  today (role-aware Cycles/Members/Setup vs member-side My schedule/etc).
 *  Defaults to `'scheduling'` so all existing callers + tests are
 *  unaffected. */
export function deriveNavLinks(
  // #2060 — `observer`: an admin account reading a team it is not on gets
  // the lead's strip (every page it opens is read-only).
  role: WorkspaceRole | "observer" | null,
  activeWorkspaceId: string | null,
  // #1666 retired the member strip, which was this flag's only consumer.
  // Kept in the signature — positional, and every caller passes it — so the
  // lead-side gate that will need it lands without re-threading call sites.
  _swapRequestsEnabled = false,
  teamKind: TeamKind = "scheduling",
  // #1282 — the per-team `manpower_planning` component. Passed through;
  // since #2048 it no longer changes the lead strip.
  manpowerPlanningEnabled = false,
): NavLinkSpec[] {
  // #753 — tasks teams use the TEAM_APPS registry nav regardless of role
  // (B7.2: Map · Members · Settings, never Cycles/Stations/Solve).
  if (teamKind === "tasks" && activeWorkspaceId) {
    return TEAM_APPS.tasks.navItems(activeWorkspaceId).map((item) => ({
      to: item.to,
      label: item.label,
      focal: item.focal,
      end: item.end,
    }));
  }

  // #1666 — a scheduling-team member has NO shell strip. Every member
  // destination now lives inside the phone frame at `/me/*` (Inbox ·
  // Calendar · Tasks + the profile sheet), and the last strip entry with
  // a home of its own — Timeline — was cut by the founder's Q2 verdict
  // once N1/N2 cards absorbed its content (design §4.2). A member who
  // lands on a shell route is redirected into the app rather than shown
  // a strip pointing back at pages the app already owns. `swapRequests`
  // is now unread on this path; it stays in the signature because the
  // tasks branch and future gates share the call site.
  if (role === "member") return [];
  if (role === "lead" || role === "co_lead" || role === "observer")
    return leadNavLinks(activeWorkspaceId, manpowerPlanningEnabled);
  // No role resolved (no memberships yet, or workspaces query still
  // loading). Render an empty strip — #1294 removed the account-scoped
  // Billing fallback, and there is no other workspace-agnostic surface
  // to offer here; the workspace switcher's "+ Create new team" stays
  // reachable from the shell chrome itself.
  return [];
}

// Module-switcher status — `active` renders the green "live" pill,
// `stub` renders the muted "soon" pill. Insights / Comply / Forecast
// shipped in Phases B / C / D (live data, real backends, worker-side
// mirrors); leaving them labelled `stub`/"soon" silently drifted into
// a documentation lie a returning lead could see at a glance. Tasks
// and Bench remain genuinely preview-only (wireframe-as-code on
// `ModulePage.tsx`) so they correctly stay `stub`. Closes Aisha UX
// log F3 friction + round-e §2.7 stale-stub finding.
//
// Exported so vitest can assert the shape directly without driving the
// Radix dropdown open from JSDOM (focus + portal rendering is brittle
// in headless tests).
export interface ModuleSwitcherEntry {
  to: string;
  label: string;
  status: "active" | "stub";
}
export const MODULES: ModuleSwitcherEntry[] = [
  { to: "/", label: "Roster", status: "active" },
  { to: "/modules/tasks", label: "Tasks", status: "stub" },
  { to: "/modules/insights", label: "Insights", status: "active" },
  { to: "/modules/forecast", label: "Forecast", status: "active" },
  { to: "/modules/bench", label: "Bench", status: "stub" },
  { to: "/modules/comply", label: "Comply", status: "active" },
];

/**
 * #2048 — show the module switcher (Roster ▾) in the top bar only when the
 * account has more than one kind of team to move between: a tasks team
 * beside its scheduling ones, or a tasks team active. For everyone else it
 * read as a fifth nav item that went nowhere useful; the module previews
 * stay one click away in the account menu (`<ModuleMenuItems/>`).
 */
export function shouldShowModuleSwitcher(
  workspaces: ReadonlyArray<{ team_kind?: string | null }> | undefined,
  activeTeamKind: TeamKind,
): boolean {
  if (activeTeamKind !== "scheduling") return true;
  return (workspaces ?? []).some((w) => w.team_kind === "tasks");
}

/**
 * Wave-6F (#479) — the team home page (`/teams/:tid`) renders edge-to-
 * edge per founder feedback (master plan §12.1 item 2). Every other
 * authenticated surface keeps the centered `max-w-7xl` posture. The
 * regex matches `/teams/<uuid-ish>` exactly (no trailing sub-route)
 * so `/teams/<tid>/setup/...`, `/teams/<tid>/audit`, etc. keep the
 * centered chrome — only the home view goes full-width.
 *
 * Wave-8A (#504) — the cycle workspace page (`/cycles/:cid` + the
 * legacy sub-route aliases like `/cycles/:cid/roster`) also goes
 * edge-to-edge so the wave-8B-anchored sidebar + main pane can mirror
 * the wave-6F home posture. Excluded: `/cycles/:cid/{submit,blocks}` —
 * those mount the legacy SubmitPage / BlocksEditor, which still expect
 * the centered chrome.
 *
 * #895 — the task-map ("Map") routes (`/teams/:tid/map`,
 * `/teams/:tid/map/members`, `/teams/:tid/map/settings`) go edge-to-
 * edge so the canvas fills the full viewport width without the
 * `max-w-7xl` side dead-space and the `py-6` vertical padding that
 * steals canvas height. #1043 adds the IA-shell Home overview
 * (`/teams/:tid/map/home`) and `board` (defensive, a future sub-surface)
 * to the same set. (PR-C retired the standalone `/map/history` route —
 * History is now a docked panel inside the bare /map window.) Only the
 * known Map sub-routes are included; any unknown
 * sub-route (e.g. `/teams/:tid/map/submit`) falls through to the centered
 * posture.
 *
 * Exported for unit-test coverage; the AppShell's render is otherwise
 * not easily snapshotable for the layout class.
 */
export function isEdgeToEdgeRoute(pathname: string): boolean {
  if (/^\/teams\/[^/]+\/?$/.test(pathname)) return true;
  // #820 (R2.3) — the full members-management page is a wide attribute
  // table that should fill the viewport (no `max-w-7xl` cap). The page
  // owns its own internal padding, matching the edge-to-edge convention.
  // #2048 — it is the People page's List tab; its "Over time" tab rides
  // the same address (`?view=over-time`).
  if (/^\/teams\/[^/]+\/members\/?$/.test(pathname)) return true;
  // `/cycles/<id>` bare OR `/cycles/<id>/<surface>` where <surface>
  // is one of the legacy tab aliases. `/cycles/<id>/submit` and
  // `/cycles/<id>/blocks` are deliberately excluded — they mount
  // separate page components (SubmitPage / BlocksEditor) that
  // pre-date the wave-8A IA rework.
  if (
    /^\/cycles\/[^/]+(\/(roster|submissions|leaves|feedback|solve|demand|fairness|hours|swaps))?\/?$/.test(
      pathname,
    )
  )
    return true;
  // #2675 — the station view: the stage fills the width (`/teams/:id/floor`).
  if (/^\/teams\/[^/]+\/floor\/?$/.test(pathname)) return true;
  // #895 — task-map routes: bare /map, /map/members, /map/settings.
  // #1043 — the IA-shell Home overview (/map/home) joins them (and `board`
  // defensively, a future Map sub-surface). PR-C (F2, 2026-05-26) RETIRED the
  // /map/history route — History is now a docked panel inside the bare /map
  // window, so it needs no route of its own. Unknown sub-paths (e.g.
  // /map/submit) are deliberately excluded.
  return /^\/teams\/[^/]+\/map(\/(home|board|members|settings))?\/?$/.test(pathname);
}
